import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { 
  AbstractControl, 
  FormControl, 
  FormsModule, 
  ReactiveFormsModule, 
  ValidationErrors, 
  Validators 
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { 
  Observable, 
  debounceTime, 
  distinctUntilChanged, 
  filter, 
  map, 
  of, 
  startWith, 
  switchMap 
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { BIQ_TOTAL_QUESTIONS, US_LOCATIONS } from '@app/utils/constants';
import { LocationOption, UserBiqData } from '@app/schemas/biq';
import { BiqButtonComponent } from '../biq-button/biq-button.component';
import { Store } from '@ngrx/store';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { GooglePlacesService } from '@app/services/google/places/google-places.service';

@Component({
  selector: 'app-biq-location',
  imports: [
    ProgressBarComponent,
    BiqButtonComponent,
    FormsModule, 
    MatFormFieldModule, 
    MatInputModule, 
    ReactiveFormsModule,
    MatAutocompleteModule,
    AsyncPipe,
  ],
  templateUrl: './location.component.html',
  styleUrl: './location.component.css'
})
export class LocationComponent implements OnInit {
  @Output() continueEmitter: EventEmitter<void> = new EventEmitter<void>();
  public questNo: number = 1;
  public totalQuestions: number = BIQ_TOTAL_QUESTIONS;
  public filteredLocations!: Observable<any[]>;
  public locations: LocationOption[] = US_LOCATIONS;
  public locationControl = new FormControl('', [
    Validators.required,
  ]);
  public storedBiqData!: UserBiqData;
  public selectedLocation: string | null = null;
  public selectedPlaceId: string | null = null;
  public availableOptions: string[] = [];

  constructor(
    private store: Store<{ userBiq: UserBiqData }>,
    private googlePlaces: GooglePlacesService
  ) {
    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value && value.location) {
        this.locationControl.setValue(value.location);
        this.selectedLocation = value.location;
      }
    });
  }

  ngOnInit() {
    this.filteredLocations = this.locationControl.valueChanges.pipe(
      startWith(''),
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value.length > 0) {
          return this.googlePlaces.getPlacePredictionsWithDetails(value).pipe(
            map((results: any[]) => {
              this.availableOptions = results.map(r => r.description);
              return results;
            })
          );
        } else {
          this.availableOptions = [];
          return of([]);
        }
      })
    );

    this.locationControl.setValidators([
      Validators.required,
      this.validateLocation.bind(this)
    ]);

    this.locationControl.updateValueAndValidity();
  }

  onLocationSelected(event: any) {
    const value = event.option.value;
    this.selectedLocation = value.description || value;
    this.selectedPlaceId = value.placeId || null;

    // Update form control with the string description, not the object
    this.locationControl.setValue(this.selectedLocation, { emitEvent: false });
    
    // Fetch and log administrative_area_level_2 when location is selected
    if (this.selectedPlaceId) {
      this.googlePlaces.getPlaceDetails(this.selectedPlaceId).subscribe(place => {
        const county = this.googlePlaces.extractAdministrativeAreaLevel2(place);
        console.log('administrative_area_level_2:', county);
      });
    }
    
    this.locationControl.setErrors(null);
    this.locationControl.updateValueAndValidity();
  }

  validateLocation = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
  
    // If selectedLocation exists and matches, it's valid
    if (this.selectedLocation === value) {
      return null;
    }
  
    // Otherwise, check if value is one of the options
    if (this.availableOptions.includes(value)) {
      return null;
    }
  
    return { invalidLocation: true };
  };

  onClickContinue() {
    this.locationControl.markAsTouched();
    if (!this.locationControl.invalid) {
      const selectedLocation = this.locationControl.value ?? '';
      const updatedBiq = {
        ...this.storedBiqData,
        location: selectedLocation,
      }
      this.store.dispatch(updateUserBiq({ user: updatedBiq }));
      this.continueEmitter.emit();
    }
  }
}
